import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

// Default Light Mode Colors - WCAG AAA compliant (7:1 contrast ratio)
export const DEFAULT_LIGHT_COLORS = {
  primary: '#0066CC',           // Kräftiges Blau
  secondary: '#6B7280',         // Mittelgrau
  background: '#FFFFFF',        // Reines Weiß
  cardBackground: '#FFFFFF',    // Karten Weiß
  textPrimary: '#111827',       // Fast Schwarz für max. Kontrast
  textSecondary: '#374151',     // Dunkles Grau
  border: '#D1D5DB',            // Mittleres Grau
  success: '#059669',           // Kräftiges Grün
  warning: '#D97706',           // Kräftiges Orange
  error: '#DC2626',             // Kräftiges Rot
  info: '#0284C7',              // Kräftiges Hellblau
  muted: '#F3F4F6',             // Sehr helles Grau
  mutedForeground: '#4B5563',   // Dunkles Grau für muted text
  accent: '#F3F4F6',            // Akzent-Hintergrund
  accentForeground: '#111827',  // Akzent-Text
  // Shadow settings
  cardShadow: 'medium',         // none, light, medium, strong
  cardBorderRadius: '0.5rem',   // Border radius for cards
};

// Shadow presets for cards
export const SHADOW_PRESETS = {
  none: 'none',
  light: '0 1px 3px 0 rgb(0 0 0 / 0.1), 0 1px 2px -1px rgb(0 0 0 / 0.1)',
  medium: '0 4px 6px -1px rgb(0 0 0 / 0.1), 0 2px 4px -2px rgb(0 0 0 / 0.1)',
  strong: '0 10px 15px -3px rgb(0 0 0 / 0.15), 0 4px 6px -4px rgb(0 0 0 / 0.15)',
  elevated: '0 20px 25px -5px rgb(0 0 0 / 0.15), 0 8px 10px -6px rgb(0 0 0 / 0.15)',
};

// Calculate contrast ratio between two colors
export const getContrastRatio = (color1, color2) => {
  const getLuminance = (hex) => {
    const rgb = parseInt(hex.slice(1), 16);
    const r = ((rgb >> 16) & 0xff) / 255;
    const g = ((rgb >> 8) & 0xff) / 255;
    const b = (rgb & 0xff) / 255;
    
    const toLinear = (c) => c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4);
    
    return 0.2126 * toLinear(r) + 0.7152 * toLinear(g) + 0.0722 * toLinear(b);
  };
  
  const l1 = getLuminance(color1);
  const l2 = getLuminance(color2);
  const lighter = Math.max(l1, l2);
  const darker = Math.min(l1, l2);
  
  return (lighter + 0.05) / (darker + 0.05);
};

// Check if contrast meets WCAG AAA (7:1)
export const meetsWCAGAAA = (foreground, background) => {
  return getContrastRatio(foreground, background) >= 7;
};

// Convert hex to HSL for CSS variables
const hexToHSL = (hex) => {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  if (!result) return '0 0% 0%';
  
  let r = parseInt(result[1], 16) / 255;
  let g = parseInt(result[2], 16) / 255;
  let b = parseInt(result[3], 16) / 255;
  
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  let h, s, l = (max + min) / 2;
  
  if (max === min) {
    h = s = 0;
  } else {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    switch (max) {
      case r: h = ((g - b) / d + (g < b ? 6 : 0)) / 6; break;
      case g: h = ((b - r) / d + 2) / 6; break;
      case b: h = ((r - g) / d + 4) / 6; break;
      default: h = 0;
    }
  }
  
  return `${Math.round(h * 360)} ${Math.round(s * 100)}% ${Math.round(l * 100)}%`;
};

const ThemeContext = createContext(null);

export const ThemeProvider = ({ children }) => {
  const [theme, setTheme] = useState(() => {
    // Initialize from localStorage
    return localStorage.getItem('theme') || 'dark';
  });
  
  const [lightColors, setLightColors] = useState(DEFAULT_LIGHT_COLORS);
  const [isLoading, setIsLoading] = useState(true);

  // Apply theme to document
  const applyTheme = useCallback((newTheme, colors = lightColors) => {
    const root = document.documentElement;
    
    if (newTheme === 'light') {
      root.classList.add('light');
      root.setAttribute('data-theme', 'light');
      
      // Apply custom light colors as CSS variables
      root.style.setProperty('--background', hexToHSL(colors.background));
      root.style.setProperty('--foreground', hexToHSL(colors.textPrimary));
      root.style.setProperty('--card', hexToHSL(colors.cardBackground));
      root.style.setProperty('--card-foreground', hexToHSL(colors.textPrimary));
      root.style.setProperty('--popover', hexToHSL(colors.cardBackground));
      root.style.setProperty('--popover-foreground', hexToHSL(colors.textPrimary));
      root.style.setProperty('--primary', hexToHSL(colors.primary));
      root.style.setProperty('--primary-foreground', '0 0% 100%');
      root.style.setProperty('--secondary', hexToHSL(colors.secondary));
      root.style.setProperty('--secondary-foreground', '0 0% 100%');
      root.style.setProperty('--muted', hexToHSL(colors.muted));
      root.style.setProperty('--muted-foreground', hexToHSL(colors.mutedForeground));
      root.style.setProperty('--accent', hexToHSL(colors.accent));
      root.style.setProperty('--accent-foreground', hexToHSL(colors.accentForeground));
      root.style.setProperty('--destructive', hexToHSL(colors.error));
      root.style.setProperty('--destructive-foreground', '0 0% 100%');
      root.style.setProperty('--border', hexToHSL(colors.border));
      root.style.setProperty('--input', hexToHSL(colors.border));
      root.style.setProperty('--ring', hexToHSL(colors.primary));
      root.style.setProperty('--success', hexToHSL(colors.success));
      root.style.setProperty('--warning', hexToHSL(colors.warning));
      
      // Additional custom properties for direct access
      root.style.setProperty('--color-primary', colors.primary);
      root.style.setProperty('--color-secondary', colors.secondary);
      root.style.setProperty('--color-background', colors.background);
      root.style.setProperty('--color-card', colors.cardBackground);
      root.style.setProperty('--color-text-primary', colors.textPrimary);
      root.style.setProperty('--color-text-secondary', colors.textSecondary);
      root.style.setProperty('--color-border', colors.border);
      root.style.setProperty('--color-success', colors.success);
      root.style.setProperty('--color-warning', colors.warning);
      root.style.setProperty('--color-error', colors.error);
      root.style.setProperty('--color-info', colors.info);
      
      // Shadow and border radius settings
      const shadowValue = SHADOW_PRESETS[colors.cardShadow] || SHADOW_PRESETS.medium;
      root.style.setProperty('--card-shadow', shadowValue);
      root.style.setProperty('--card-border-radius', colors.cardBorderRadius || '0.5rem');
    } else {
      root.classList.remove('light');
      root.removeAttribute('data-theme');
      
      // Remove custom properties to use defaults
      const props = [
        '--background', '--foreground', '--card', '--card-foreground',
        '--popover', '--popover-foreground', '--primary', '--primary-foreground',
        '--secondary', '--secondary-foreground', '--muted', '--muted-foreground',
        '--accent', '--accent-foreground', '--destructive', '--destructive-foreground',
        '--border', '--input', '--ring', '--success', '--warning',
        '--color-primary', '--color-secondary', '--color-background', '--color-card',
        '--color-text-primary', '--color-text-secondary', '--color-border',
        '--color-success', '--color-warning', '--color-error', '--color-info',
        '--card-shadow', '--card-border-radius'
      ];
      props.forEach(prop => root.style.removeProperty(prop));
    }
  }, [lightColors]);

  // Load theme config from API on mount
  useEffect(() => {
    const loadThemeConfig = async () => {
      try {
        // Load admin-configured light colors
        const response = await axios.get(`${API_URL}/app-config/theme`);
        if (response.data && response.data.lightColors) {
          setLightColors({ ...DEFAULT_LIGHT_COLORS, ...response.data.lightColors });
        }
      } catch (error) {
      } finally {
        setIsLoading(false);
      }
    };
    
    loadThemeConfig();
  }, []);

  // Apply theme whenever it changes
  useEffect(() => {
    if (!isLoading) {
      applyTheme(theme, lightColors);
    }
  }, [theme, lightColors, isLoading, applyTheme]);

  // Toggle theme
  const toggleTheme = useCallback(() => {
    const newTheme = theme === 'dark' ? 'light' : 'dark';
    setTheme(newTheme);
    localStorage.setItem('theme', newTheme);
    
    // Save to user preferences via API (fire and forget)
    const token = localStorage.getItem('token');
    if (token) {
      axios.patch(`${API_URL}/users/me/preferences`, { theme: newTheme }).catch(() => {});
    }
  }, [theme]);

  // Set specific theme
  const setThemeMode = useCallback((newTheme) => {
    setTheme(newTheme);
    localStorage.setItem('theme', newTheme);
    
    const token = localStorage.getItem('token');
    if (token) {
      axios.patch(`${API_URL}/users/me/preferences`, { theme: newTheme }).catch(() => {});
    }
  }, []);

  // Update light colors (admin only)
  const updateLightColors = useCallback(async (newColors) => {
    const mergedColors = { ...lightColors, ...newColors };
    setLightColors(mergedColors);
    
    if (theme === 'light') {
      applyTheme('light', mergedColors);
    }
    
    // Save to API
    try {
      await axios.patch(`${API_URL}/app-config/theme`, { lightColors: mergedColors });
      return true;
    } catch (error) {
      return false;
    }
  }, [lightColors, theme, applyTheme]);

  // Reset light colors to defaults
  const resetLightColors = useCallback(async () => {
    setLightColors(DEFAULT_LIGHT_COLORS);
    
    if (theme === 'light') {
      applyTheme('light', DEFAULT_LIGHT_COLORS);
    }
    
    try {
      await axios.patch(`${API_URL}/app-config/theme`, { lightColors: DEFAULT_LIGHT_COLORS });
      return true;
    } catch (error) {
      return false;
    }
  }, [theme, applyTheme]);

  // Load user preference on login
  const loadUserPreference = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/users/me/preferences`);
      if (response.data && response.data.theme) {
        setTheme(response.data.theme);
        localStorage.setItem('theme', response.data.theme);
      }
    } catch (error) {
      // Use localStorage value
    }
  }, []);

  const value = {
    theme,
    isDark: theme === 'dark',
    isLight: theme === 'light',
    toggleTheme,
    setTheme: setThemeMode,
    lightColors,
    updateLightColors,
    resetLightColors,
    loadUserPreference,
    isLoading,
    getContrastRatio,
    meetsWCAGAAA,
    DEFAULT_LIGHT_COLORS,
    SHADOW_PRESETS,
  };

  return (
    <ThemeContext.Provider value={value}>
      {children}
    </ThemeContext.Provider>
  );
};

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};

export default ThemeContext;
